/**
 * @desc: Filter 保存筛选能力桥接层（预设选择 + 保存弹窗）
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import { useIntl } from 'umi';
import { Button, Icon } from 'tea-component';
import { Bubble } from '@home/components/TeaComponents';
import FilterPresetSelect from '@intelligence/v3/components/FormComponents/FilterPresetSelect';
import FilterStickyHeaderIndicator from '../FilterStickyHeaderIndicator';
import SaveFilterPopup from '../SaveFilterPopup';
import useFilterSaveHook from '@intelligence/hooks/useFilterSaveHook';

import type { IFilterPresetOption } from '@intelligence/v3/components/FormComponents/FilterPresetSelect';
import type { IFilterItemConfig, IFilterValues } from '@intelligence/v3/filterCore/types';

export interface IFilterSaveFeatureRenderProps {
  /** 注入到筛选器项前的 My Filter 选择器 */
  beforeItemsContent: React.ReactNode;
  /** 注入到吸顶摘要前的 My Filter 静态标识 */
  stickyBeforeSummaryContent: React.ReactNode;
  /** 注入到操作区的保存方案按钮和弹窗 */
  innerExtraActions: React.ReactNode;
  /** draft 字段变化时用于判断是否清空当前预设选中态 */
  handleDraftFieldChange: (field: string, fieldValue: any) => void;
  /** Reset 时清空当前预设选中态 */
  handleReset: () => void;
}

interface IFilterSaveRecord {
  name?: string;
  data?: string;
}

const hasOwnField = (target: IFilterValues, field: string): boolean => {
  return Object.prototype.hasOwnProperty.call(target || {}, field);
};

const getResolvedPresetValues = (
  presetValues: IFilterValues | null,
  currentValue: IFilterValues,
): IFilterValues | null => {
  if (!presetValues) {
    return null;
  }

  const resolvedValues = Object.keys(presetValues).reduce<IFilterValues>((result, field) => {
    if (hasOwnField(currentValue, field)) {
      result[field] = currentValue[field];
    }

    return result;
  }, {});

  return Object.keys(resolvedValues).length > 0 ? resolvedValues : null;
};

interface IFilterSaveFeatureProps {
  /** Filter 加载态，加载时保存按钮禁用 */
  loading?: boolean;
  /** 保存方案类型，不同类型的方案列表隔离 */
  filterSaveType?: string;
  /** 当前筛选器配置，用于保存弹窗展示字段和预览 */
  items: IFilterItemConfig[];
  /** 当前 draft value，保存方案时从这里取值 */
  currentValue: IFilterValues;
  /** 选择预设后把预设值加载回 Filter draft */
  onLoad: (values: IFilterValues) => void;
  /** 外部清空预设选中态的令牌 */
  presetClearToken?: number;
  /** render props，把 My Filter 能力注入 FilterView */
  children: (props: IFilterSaveFeatureRenderProps) => React.ReactElement | null;
}

/**
 * 判断当前 draft 是否仍完全包含已选预设的字段值。
 * 预设只保存部分字段，因此只按预设字段做匹配。
 */
const isPresetMatched = (presetValues: IFilterValues | null, currentValue: IFilterValues): boolean => {
  if (!presetValues) {
    return false;
  }

  return Object.keys(presetValues).every((field) => {
    return isEqual(presetValues[field], currentValue[field]);
  });
};

const shouldClearPresetByFieldChange = (
  presetValues: IFilterValues | null,
  field: string,
  fieldValue: unknown,
): boolean => {
  if (!presetValues || !hasOwnField(presetValues, field)) {
    return false;
  }

  return !isEqual(presetValues[field], fieldValue);
};

const FilterSaveFeature: React.FC<IFilterSaveFeatureProps> = (props) => {
  const { loading = false, filterSaveType = '', items, currentValue, onLoad, presetClearToken, children } = props;

  const { formatMessage } = useIntl();

  const [selectedPresetName, setSelectedPresetName] = useState('');
  const [savePopupVisible, setSavePopupVisible] = useState(false);
  const selectedPresetValuesRef = useRef<IFilterValues | null>(null);
  const isApplyingPresetRef = useRef(false);
  const applyPresetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCurrentValueRef = useRef(currentValue);

  latestCurrentValueRef.current = currentValue;

  const { filterData, getFilterSaveData, getFilterItemByFilterName, removeFilterSave, updateFilterSave } =
    useFilterSaveHook({
      filterSaveType,
      useFilterSaveListByType: true,
    });

  useEffect(() => {
    if (!filterSaveType) {
      return;
    }

    getFilterSaveData().catch((error) => {
      console.debug('[dbi] [FilterSaveFeature] 获取筛选方案列表失败', error);
    });
  }, [filterSaveType]);

  useEffect(() => {
    if (presetClearToken === undefined) {
      return;
    }

    clearSelectedPresetState();
  }, [presetClearToken]);

  const presetOptions = useMemo<IFilterPresetOption[]>(() => {
    const filterDataList = Array.isArray(filterData) ? (filterData as IFilterSaveRecord[]) : [];

    return filterDataList.reduce((result: IFilterPresetOption[], item) => {
      const name = item?.name || '';

      if (name) {
        result.push({
          value: name,
          text: name,
        });
      }

      return result;
    }, []);
  }, [filterData]);

  const existingNames = useMemo(() => {
    return (Array.isArray(filterData) ? (filterData as IFilterSaveRecord[]) : []).map((item) => item?.name || '');
  }, [filterData]);

  const isAtSaveLimit = (filterData?.length ?? 0) >= 10;

  const clearPresetApplyingTimer = (): void => {
    if (applyPresetTimerRef.current) {
      clearTimeout(applyPresetTimerRef.current);
      applyPresetTimerRef.current = null;
    }
  };

  /** 统一清空预设选择状态，避免删除、重置、无效加载分支状态不一致 */
  const clearSelectedPresetState = (): void => {
    clearPresetApplyingTimer();
    setSelectedPresetName('');
    selectedPresetValuesRef.current = null;
    isApplyingPresetRef.current = false;
  };

  const finishApplyingPreset = (): void => {
    clearPresetApplyingTimer();

    if (!isApplyingPresetRef.current) {
      return;
    }

    const resolvedPresetValues = getResolvedPresetValues(
      selectedPresetValuesRef.current,
      latestCurrentValueRef.current,
    );

    if (!resolvedPresetValues) {
      clearSelectedPresetState();
      return;
    }

    selectedPresetValuesRef.current = resolvedPresetValues;
    isApplyingPresetRef.current = false;
  };

  /** 选择预设后只加载到 draft，不自动触发 Confirm */
  const handlePresetChange = (name: string): void => {
    if (!name) {
      clearSelectedPresetState();
      return;
    }

    const savedItem = getFilterItemByFilterName(name);

    if (savedItem !== null && typeof savedItem === 'object') {
      setSelectedPresetName(name);
      selectedPresetValuesRef.current = savedItem as IFilterValues;
      isApplyingPresetRef.current = true;
      onLoad(savedItem as IFilterValues);
      clearPresetApplyingTimer();
      applyPresetTimerRef.current = setTimeout(() => {
        finishApplyingPreset();
      }, 0);
      return;
    }

    clearSelectedPresetState();
  };

  /** 删除预设后，如果删除的是当前选中项，同步清空选中态 */
  const handlePresetDelete = (name: string): void => {
    removeFilterSave({ name })
      .then((isSuccess) => {
        if (isSuccess && selectedPresetName === name) {
          clearSelectedPresetState();
        }
      })
      .catch((error) => {
        console.debug('[dbi] [FilterSaveFeature] 删除筛选方案失败', error);
      });
  };

  /** 保存成功后把新方案标记为当前选中方案 */
  const handleSaved = (name: string, savedValues?: IFilterValues): void => {
    clearPresetApplyingTimer();
    setSelectedPresetName(name);
    selectedPresetValuesRef.current = savedValues || null;
    isApplyingPresetRef.current = false;
  };

  useEffect(() => {
    return () => {
      clearPresetApplyingTimer();
    };
  }, []);

  useEffect(() => {
    if (!selectedPresetName) {
      return;
    }

    const selectedPresetValues = selectedPresetValuesRef.current;

    if (!selectedPresetValues) {
      return;
    }

    const isMatched = isPresetMatched(selectedPresetValues, currentValue);

    if (isApplyingPresetRef.current) {
      if (isMatched) {
        finishApplyingPreset();
      }

      return;
    }

    if (!isMatched) {
      clearSelectedPresetState();
    }
  }, [currentValue, selectedPresetName]);

  /** 只在用户改动了 preset 自己保存过的字段时清空选中态 */
  const handleDraftFieldChange = (field: string, fieldValue: any): void => {
    if (selectedPresetName && shouldClearPresetByFieldChange(selectedPresetValuesRef.current, field, fieldValue)) {
      clearSelectedPresetState();
    }
  };

  /** Reset 后不再保留 My Filter 选中态 */
  const handleReset = (): void => {
    clearSelectedPresetState();
  };

  return children({
    beforeItemsContent: (
      <FilterPresetSelect
        value={selectedPresetName}
        options={presetOptions}
        onChange={handlePresetChange}
        onDelete={handlePresetDelete}
      />
    ),
    stickyBeforeSummaryContent: (
      <FilterStickyHeaderIndicator
        icon={<Icon type="filter" className="dbi-filter-sticky-header__saved-filter-icon" />}
        text="dbi_filterSave_my_filter"
      />
    ),
    innerExtraActions: (
      <>
        <Bubble dark content={!isAtSaveLimit ? '' : formatMessage({ id: 'dbi_filterSave_my_filter_top10' })}>
          <Button type="weak" disabled={loading} onClick={() => setSavePopupVisible(true)}>
            {formatMessage({ id: 'dbi_filterSave_filter_setting' })}
          </Button>
        </Bubble>
        {savePopupVisible && (
          <SaveFilterPopup
            visible={savePopupVisible}
            currentValue={currentValue}
            items={items}
            existingNames={existingNames}
            isAtSaveLimit={isAtSaveLimit}
            updateFilterSave={updateFilterSave}
            onSaved={handleSaved}
            onClose={() => setSavePopupVisible(false)}
          />
        )}
      </>
    ),
    handleDraftFieldChange,
    handleReset,
  });
};

export default FilterSaveFeature;
